import { useState, useEffect } from 'react';
import { Plus, X, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import api from '../api';

const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};
const SECTION = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,35%)',
  textTransform: 'uppercase', letterSpacing: '0.15em',
  marginBottom: 10, borderBottom: '1px solid hsl(0,0%,12%)', paddingBottom: 6,
};

const EMPTY = {
  name: '', code: '', gstin: '', pan: '',
  city: '', state: '', pincode: '',
  contact_name: '', contact_phone: '', contact_email: '',
  bank_account_no: '', bank_ifsc: '', bank_name: '',
  payment_terms: 'Net 30', lead_time_days: 0, rating: 0,
};

function VendorModal({ vendor, onClose, onSaved }) {
  const isEdit = !!vendor?.id;
  const [form, setForm] = useState(isEdit ? {
    name: vendor.name || '',
    code: vendor.code || '',
    gstin: vendor.gstin || '',
    pan: vendor.pan || '',
    city: vendor.registered_address?.city || vendor.city || '',
    state: vendor.registered_address?.state || vendor.state || '',
    pincode: vendor.registered_address?.pincode || '',
    contact_name: vendor.contact_name || '',
    contact_phone: vendor.contact_phone || '',
    contact_email: vendor.contact_email || '',
    bank_account_no: vendor.bank_account_no || '',
    bank_ifsc: vendor.bank_ifsc || '',
    bank_name: vendor.bank_name || '',
    payment_terms: vendor.payment_terms || 'Net 30',
    lead_time_days: vendor.lead_time_days || 0,
    rating: vendor.rating || 0,
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      registered_address: { city: form.city, state: form.state, pincode: form.pincode },
      lead_time_days: parseInt(form.lead_time_days) || 0,
      rating: parseFloat(form.rating) || 0,
    };
    try {
      if (isEdit) { await api.put(`/vendors/${vendor.id}`, payload); }
      else { await api.post('/vendors', payload); }
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 600, padding: 24, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>
            {isEdit ? 'Edit Vendor' : 'Add Vendor'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Basic Info</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Vendor Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  required style={INP} data-testid="vendor-name" /></div>
              <div><label style={LBL}>Code</label>
                <input value={form.code} onChange={e => set('code', e.target.value)}
                  placeholder="VEN-XXX" style={INP} /></div>
              <div><label style={LBL}>GSTIN</label>
                <input value={form.gstin} onChange={e => set('gstin', e.target.value)}
                  placeholder="22AAAAA0000A1Z5" style={INP} /></div>
              <div><label style={LBL}>PAN</label>
                <input value={form.pan} onChange={e => set('pan', e.target.value)}
                  placeholder="AAAAA0000A" style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Address &amp; Contact</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>State</label>
                <input value={form.state} onChange={e => set('state', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Pincode</label>
                <input value={form.pincode} onChange={e => set('pincode', e.target.value)} style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Contact Name</label>
                <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Phone</label>
                <input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Email</label>
                <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Bank &amp; Payment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={LBL}>Bank Name</label>
                <input value={form.bank_name} onChange={e => set('bank_name', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Payment Terms</label>
                <input value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)}
                  placeholder="Net 30" style={INP} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Account No</label>
                <input value={form.bank_account_no} onChange={e => set('bank_account_no', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>IFSC</label>
                <input value={form.bank_ifsc} onChange={e => set('bank_ifsc', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={SECTION}>Vendor Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={LBL}>Lead Time (days)</label>
                <input type="number" min="0" value={form.lead_time_days}
                  onChange={e => set('lead_time_days', e.target.value)} style={INP} /></div>
              <div><label style={LBL}>Rating (0–5)</label>
                <input type="number" min="0" max="5" step="0.1" value={form.rating}
                  onChange={e => set('rating', e.target.value)} style={INP} /></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-vendor-btn">
              {saving ? 'Saving...' : isEdit ? 'Update Vendor' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendors', { params: { search } });
      setVendors(r.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (v) => {
    await api.patch(`/vendors/${v.id}/status`, { status: v.status === 'active' ? 'inactive' : 'active' });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
            Vendors
          </h1>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)', marginTop: 4 }}>
            Raw material &amp; product suppliers
          </p>
        </div>
        <button onClick={() => { setEditVendor(null); setShowModal(true); }}
          className="btn-primary" data-testid="add-vendor-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
          <Plus size={14} /> Add Vendor
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 320 }}>
        <MagnifyingGlass size={14} style={{ position: 'absolute', left: 10, top: '50%',
          transform: 'translateY(-50%)', color: 'hsl(0,0%,40%)', pointerEvents: 'none' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, GSTIN..."
          data-testid="vendor-search"
          style={{ ...INP, paddingLeft: 30 }} />
      </div>

      <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 80px 80px 110px',
          background: 'hsl(0,0%,8%)', padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,14%)' }}>
          {['Name','Code','GSTIN','Contact','Lead Days','Rating','Actions'].map(h => (
            <span key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</span>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,40%)' }}>Loading...</div>
        ) : vendors.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, color: 'hsl(0,0%,35%)' }}>No vendors found</div>
        ) : vendors.map(v => (
          <div key={v.id} data-testid="vendor-row"
            style={{ display: 'grid', gridTemplateColumns: '200px 100px 160px 140px 80px 80px 110px',
              padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,10%)', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                color: v.status === 'inactive' ? 'hsl(0,0%,40%)' : 'hsl(0,0%,90%)' }}>{v.name}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,35%)', marginTop: 2 }}>
                {[v.registered_address?.city || v.city, v.registered_address?.state || v.state].filter(Boolean).join(', ')}
              </div>
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,55%)' }}>{v.code || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,60%)' }}>{v.gstin || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,70%)' }}>{v.contact_name || '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(0,0%,60%)', textAlign: 'center' }}>{v.lead_time_days ?? '—'}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: 'hsl(43,96%,56%)' }}>{v.rating ? Number(v.rating).toFixed(1) : '—'}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button onClick={() => { setEditVendor(v); setShowModal(true); }}
                data-testid="edit-vendor-btn"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: 'hsl(0,0%,50%)', padding: 0 }}>
                <PencilSimple size={13} />
              </button>
              <button onClick={() => toggleStatus(v)}
                data-testid="toggle-vendor-status-btn"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  padding: '2px 8px', cursor: 'pointer',
                  background: v.status === 'active' ? 'hsl(0,0%,12%)' : 'hsl(0,0%,10%)',
                  border: `1px solid ${v.status === 'active' ? 'hsl(142,50%,35%)' : 'hsl(0,0%,25%)'}`,
                  color: v.status === 'active' ? 'hsl(142,60%,50%)' : 'hsl(0,0%,45%)' }}>
                {v.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <VendorModal
          vendor={editVendor}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
